import { useCallback, useEffect, useRef, useState } from 'react';

const DEFAULT_HIGHLIGHT_DURATION_MS = 1500;

interface UseHighlightedItemResult {
    highlightedItemId: string | null;
    highlightItem: (itemId: string) => void;
}

/**
 * Tracks which list item should be visually highlighted for a short period.
 *
 * A newly appended item is highlighted automatically, so any "add" button
 * gives the same feedback without wiring. Reorder and other manual cases
 * call highlightItem directly.
 *
 * @param {string[]} itemIds - Ordered ids of the rendered items. Memoize it.
 * @param {number} durationMs - How long the highlight stays visible
 * @returns {UseHighlightedItemResult} Currently highlighted id and a trigger
 */
const useHighlightedItem = (itemIds: string[], durationMs: number = DEFAULT_HIGHLIGHT_DURATION_MS): UseHighlightedItemResult => {
    const [highlightedItemId, setHighlightedItemId] = useState<string | null>(null);
    const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const prevCountRef = useRef(itemIds.length);

    // Clear timeout on unmount
    useEffect(() => {
        return () => {
            if (timeoutRef.current) {
                clearTimeout(timeoutRef.current);
            }
        };
    }, []);

    const highlightItem = useCallback(
        (itemId: string) => {
            setHighlightedItemId(itemId);

            if (timeoutRef.current) {
                clearTimeout(timeoutRef.current);
            }

            timeoutRef.current = setTimeout(() => {
                setHighlightedItemId(null);
            }, durationMs);
        },
        [durationMs]
    );

    // Highlight a newly appended item
    useEffect(() => {
        const prevCount = prevCountRef.current;

        prevCountRef.current = itemIds.length;

        // Only react to a single item appended to an already populated list
        if (prevCount === 0 || itemIds.length !== prevCount + 1) return;

        highlightItem(itemIds[itemIds.length - 1]);
    }, [itemIds, highlightItem]);

    return { highlightedItemId, highlightItem };
};

export default useHighlightedItem;
